import React, { FC, useContext } from 'react';
import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import PencilToLine from '@gravity-ui/icons/PencilToLine';
import { Button, DropdownMenu, Icon, Text } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import moment from 'moment';
import { STATUS_TO_THEME_MAP, getDisplayDraftStatus } from '../OperationStatus/OperationStatus';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { DraftStatus } from 'client/model/draft';
import { InstallableApp, AliceApp } from 'client/model/skill';
import { OperationType } from 'client/model/operation';
import { useIsDraft } from 'client/hooks/useIsDraft';
import * as keyset from 'i18n/DraftsStatuses';

import styles from './DeploymentStatus.module.scss';

const t = i18n(keyset);

export interface StatusProps {
    aliceApp: AliceApp;
}

const getDraftIcon = (draftStatus: DraftStatus | OperationType) =>
    STATUS_TO_THEME_MAP[draftStatus]?.icon ? (
        <Text color={STATUS_TO_THEME_MAP[draftStatus]?.color}>{STATUS_TO_THEME_MAP[draftStatus]?.icon}</Text>
    ) : (
        <Icon data={PencilToLine} />
    );

export const DeploymentStatus: FC = () => {
    const { aliceApp, lastOperationType = OperationType.SkillCreated } = useContext(CurrentAliceAppContext)!;
    const { isDraft, setIsDraft } = useIsDraft(aliceApp.channel, aliceApp.id);
    const installableApp = aliceApp as InstallableApp;

    let publishedCard = null;

    if (aliceApp.onAir) {
        const version =
            installableApp?.apkInfoWithTargets?.versionCode || installableApp?.draft?.apkInfoWithTargets?.versionCode;
        const updatedAt = moment(installableApp.updatedAt).format('DD/MM/YY');

        publishedCard = (
            <Button
                className={clx(styles.statusContainer, !isDraft && styles.statusContainer_activeRoute)}
                onClick={() => setIsDraft(false, aliceApp.channel)}
                view="flat"
            >
                <div className={styles.row__content}>
                    <Icon className={styles.fillGreen} data={CircleCheckFill} />
                    <span className={styles.publishedLabel__short}>{t('published-short')}</span>
                    <span className={styles.publishedLabel__long}>{t('published-long')}</span>
                </div>
                <div className={styles.row__version}>
                    {version}
                    {' • '}
                    {updatedAt}
                </div>
            </Button>
        );
    }

    return (
        <div className={styles.container}>
            {publishedCard}
            <Button
                className={clx(styles.statusContainer, isDraft && styles.statusContainer_activeRoute)}
                onClick={() => setIsDraft(true, aliceApp.channel)}
                view="flat"
            >
                <div className={styles.row__content}>
                    {getDraftIcon(getDisplayDraftStatus(true, aliceApp.draft.status, lastOperationType))}
                    {t('draft')}
                </div>
            </Button>
        </div>
    );
};

export const DeploymentStatusMobile: FC = () => {
    const { aliceApp, lastOperationType = OperationType.SkillCreated } = useContext(CurrentAliceAppContext)!;
    const { isDraft, setIsDraft } = useIsDraft(aliceApp.channel, aliceApp.id);

    const RenderOptions = {
        RenderOnAir: (
            <Button
                className={styles.mobileSelectRow}
                view="flat"
                onClick={() => setIsDraft(false, aliceApp.channel)}
                disabled={!aliceApp.onAir}
            >
                <Icon className={styles.fillGreen} data={CircleCheckFill} />
                {t('published-short')}
            </Button>
        ),

        RenderDraft: (
            <Button className={styles.mobileSelectRow} view="flat" onClick={() => setIsDraft(true, aliceApp.channel)}>
                <div className={styles.mobileSelectRow}>
                    {getDraftIcon(getDisplayDraftStatus(true, aliceApp.draft.status, lastOperationType))}
                    {t('draft')}
                </div>
            </Button>
        ),
    };

    return (
        <DropdownMenu
            renderSwitcher={(props) => (
                <div {...props} className={styles.mobileContainer}>
                    {isDraft ? RenderOptions.RenderDraft : RenderOptions.RenderOnAir}
                </div>
            )}
            items={
                isDraft
                    ? [
                          {
                              text: RenderOptions.RenderOnAir,
                              action: () => aliceApp.onAir && setIsDraft(false, aliceApp.channel),
                          },
                      ]
                    : [
                          {
                              text: RenderOptions.RenderDraft,
                              action: () => setIsDraft(true, aliceApp.channel),
                          },
                      ]
            }
        />
    );
};
